import { isTrackingDeclined } from '..';
import { log } from '../log';
import {
  GAParams,
  Config,
  DEFAULTS,
  state,
  queue,
  textFromNode,
  fieldName,
  makeAnalyticsHelpers,
  isUUID,
} from './helpers';

declare global {
  interface Window {
    FS?: {
      setUserVars: (traits?: { [key: string]: any }) => void;
    };
    gtag?: (...args: any[]) => void;
    gtmTrack?: (eventName: string, params: GAParams) => void;
    __EA_ANALYTICS_INIT__?: symbol; // who owns current init
  }
}

(globalThis as any).window.dataLayer =
  (globalThis as any).window.dataLayer || [];

////////////////////////////////- new analytics - /////////////////////////
/*
 * SUMMARY:
 * Tracks user interactions (clicks, form changes, text input)
 * and sends structured events to Google Analytics 4 (via gtag) and optionally
 * mirrors them to the Google Tag Manager dataLayer.
 *
 * This will track all events throughout the app
 *
 * Usage:
 * 1. Call initAnalytics() early in src\components\base\app\index.tsx
 *    include optional config setting if required to override DEFAULT
 *    EG:
 *    initAnalytics({
 *      allowedParams: ['action','label','category','attribute1','attribute2','attribute3','attribute4'],
 *      captureTextValue: true,
 *      valueMask: (v) => v.replace(/[^\w\-]/g, '').slice(0, 32),
 *      mirrorToDataLayer: false,
 *    });
 * 2. Use window.gtmTrack(eventName, params) for manual tracking.
 * 3. Call disposer returned by initAnalytics() on teardown.
 * 4. Add the consolidated data tag into the component for customized tracking:
 *    data-gtm-dl={JSON.stringify({
            action: 'click', // This is optional and will be pulled from the event if not here.
            category: 'Custom Category',  // This is optional and will be pulled from the event if not here.
            label: 'Custom Label', // This is optional and will be pulled from the event if not here.
            attribute1: 'file a claim',
            attribute2: string or variable,
            attribute3: string or variable,
            attribute4: string or variable,
          })}
 *    and optionally data-gtm-event="cta_click"
 *
 * *** Caveats - KENDO UI ***
 * Some Kendo UI components might not register as Many Kendo widgets raise custom events (select, change, open, etc.)
 * or trigger actions on mousedown, pointerup, or keyboard events—not on a DOM click.
 * please see readme file in this folder.
 */

// initAnalytics: Initialize analytics tracking and attach event listeners
export function initAnalytics(partial?: Partial<Config>) {
  if (isTrackingDeclined()) {
    return;
  }
  if ((globalThis as any).window === undefined) return () => {};

  // If already initialized by another call, just return a no-op disposer.
  if (
    (globalThis as any).window.__EA_ANALYTICS_INIT__ &&
    state.instanceKey &&
    (globalThis as any).window.__EA_ANALYTICS_INIT__ !== state.instanceKey
  ) {
    return () => {};
  }

  const CONFIG: Config = { ...DEFAULTS, ...partial } as Config;
  const instanceKey = Symbol('ea-init');
  (globalThis as any).window.__EA_ANALYTICS_INIT__ = instanceKey;
  state.instanceKey = instanceKey;

  // Create helpers that are bound to this CONFIG instance
  const {
    mergeParams,
    attrsToParams,
    deliver,
    handleTextInput,
    hasGtmDlAndAttribute1,
  } = makeAnalyticsHelpers(CONFIG);

  // ------ GA4 sender / queue
  state.gtagCheckId = (globalThis as any).window.setInterval(() => {
    if (typeof (globalThis as any).window.gtag === 'function') {
      if (state.gtagCheckId) clearInterval(state.gtagCheckId);
      state.gtagCheckId = undefined;
      while (queue.length) {
        const ev = queue.shift()!;
        (globalThis as any).window.gtag('event', ev.name, ev.params);
      }
    }
  }, 100);

  // ---------------- Internal Event handlers
  // clickHandler: Track clicks on buttons, links, and custom elements
  state.clickHandler = (e: Event) => {
    const target = e.target as Element | null;
    const el =
      target &&
      (target.closest('[data-gtm-dl]') ||
        target.closest('button, a, [role="button"], [role="listbox"]'));
    if (!el) return;

    // Enforce gating - make sure data attribute: attribute1 exists
    if (!hasGtmDlAndAttribute1(el)) return;

    // Handle elements explicitly marked for GTM tracking (data-gtm-dl).
    const declared = el.closest('[data-gtm-dl]');

    if (declared) {
      const tagName = declared.nodeName.toLowerCase();
      // textAre and input are handled by onblur
      if (tagName === 'textarea' || tagName === 'input') return;
      const eventName =
        (declared as HTMLElement).dataset.gtmEvent || 'ui_click';
      const base = {
        action: 'click',
        label:
          textFromNode(declared) ||
          declared.getAttribute('aria-label') ||
          (declared as HTMLElement).getAttribute?.('data-label') ||
          '',
        category: (declared as HTMLElement).dataset.gtmCategory || 'button',
      } as Record<string, any>;

      const isListbox = declared.getAttribute('role') === 'listbox';
      if (!isListbox && !hasGtmDlAndAttribute1(declared)) return;

      deliver(eventName, mergeParams(base, attrsToParams(declared)));
      return;
    }

    // Handle custom (Kendo) dropdowns
    // don't enforce gating for Kendo elements
    // Once a solution is found for Kendo components - we can add gating
    if (el.getAttribute('role') === 'listbox') {
      const base: Record<string, any> = {
        category: 'dropdown',
        action: 'select',
        label: '',
      };
      // Find the associated label text
      const dropdownId = el.getAttribute('id');
      let labelText = '';
      if (dropdownId) {
        const labelEl = document.querySelector(`label[for="${dropdownId}"]`);
        if (labelEl) {
          labelText = labelEl.textContent?.trim() || '';
        }
      }
      if (!labelText) {
        labelText = fieldName(el);
      }
      // remove duplicate dropdown clicks returning UUID in place of label
      if (isUUID(labelText)) return;
      (base as any).label = labelText;
      deliver('dropdown_select', mergeParams(base, attrsToParams(el)));
      return;
    }

    // Handle generic clickable elements (buttons and links).
    const node = el.nodeName.toLowerCase();
    if (
      node === 'button' ||
      node === 'a' ||
      el.getAttribute('role') === 'button'
    ) {
      // Enforce gating - make sure data attribute: attribute1 exists
      if (!hasGtmDlAndAttribute1(el)) return;

      const base = {
        action: 'click',
        label: textFromNode(el),
        category: node === 'a' ? 'link' : 'button',
      };
      deliver('ui_click', mergeParams(base, attrsToParams(el)));
    }
  };

  // changeHandler: Track changes in selects, radios, checkboxes, and text inputs
  state.changeHandler = (e: Event) => {
    const el = e.target as HTMLInputElement | HTMLSelectElement | null;
    if (!el) return;
    const tag = (el as Element).tagName.toLowerCase();
    const type = (el as HTMLInputElement).type?.toLowerCase?.() || '';

    // Enforce gating - make sure data attribute: attribute1 exists
    if (!hasGtmDlAndAttribute1(el)) return;

    if (tag === 'select') {
      const base: Record<string, any> = {
        category: 'select',
        action: 'select_change',
        label: '',
      };
      const sel = el as HTMLSelectElement;
      const opt = sel.options[sel.selectedIndex];
      (base as any).label =
        (opt && (opt.dataset.label || opt.textContent || opt.value)) ||
        fieldName(el);
      deliver('select_change', mergeParams(base, attrsToParams(el)));
      return;
    }

    if (type === 'radio') {
      const base: Record<string, any> = {
        category: 'radio',
        action: 'radio_select',
        label: '',
      };
      (base as any).label = (() => {
        const id = (el as HTMLInputElement).id;
        if (id) {
          const lbl = document.querySelector(`label[for="${id}"]`);
          if (lbl) return (lbl.textContent || '').trim();
        }
        return (el as HTMLInputElement).value || fieldName(el);
      })();
      deliver('input_select', mergeParams(base, attrsToParams(el)));
      return;
    }

    if (type === 'checkbox') {
      const base: Record<string, any> = {
        category: 'checkbox',
        action: (el as HTMLInputElement).checked
          ? 'checkbox_check'
          : 'checkbox_uncheck',
        label: fieldName(el),
      };
      const p = mergeParams(base, attrsToParams(el));
      (p as any).checked = !!(el as HTMLInputElement).checked;
      deliver('input_select', p);
      return;
    }

    if (
      CONFIG.trackTextOn === 'change' &&
      (tag === 'textarea' ||
        [
          'text',
          'search',
          'email',
          'tel',
          'url',
          'number',
          'password',
        ].includes(type))
    ) {
      handleTextInput(el as HTMLInputElement | HTMLTextAreaElement);
    }
  };

  // blurHandler: Track text input on blur if configured
  state.blurHandler = (e: Event) => {
    if (CONFIG.trackTextOn !== 'blur') return;

    const el = e.target as HTMLInputElement | HTMLTextAreaElement | null;
    if (!el) return;
    const tag = (el as Element).tagName.toLowerCase();
    const type = (el as HTMLInputElement).type?.toLowerCase?.() || '';

    if (
      tag === 'textarea' ||
      ['text', 'search', 'email', 'tel', 'url', 'number', 'password'].includes(
        type,
      )
    ) {
      // Enforce gating - make sure data attribute: attribute1 exists
      if (!hasGtmDlAndAttribute1(el)) return;

      handleTextInput(el);
    }
  };

  // Public API
  (globalThis as any).window.gtmTrack = function (
    eventName: string,
    params: GAParams,
  ) {
    // Gate public API calls unless attribute3 is provided
    if (
      !params ||
      (params as any).attribute3 == null ||
      String((params as any).attribute3).trim() === ''
    ) {
      return;
    }
    deliver(eventName, params as Record<string, any>);
  };

  // Attach delegated listeners (capture phase true so we run early/consistently)
  if (state.clickHandler) {
    document.addEventListener('click', state.clickHandler, true);
  }
  if (state.changeHandler) {
    document.addEventListener('change', state.changeHandler, true);
  }
  if (state.blurHandler) {
    document.addEventListener('blur', state.blurHandler, true);
  }

  // Return disposer: remove listeners, clear interval, and clean our globals
  return () => {
    // Only the owning instance should clean globals
    const isOwner =
      (globalThis as any).window.__EA_ANALYTICS_INIT__ === instanceKey;
    if (state.clickHandler)
      document.removeEventListener('click', state.clickHandler, true);
    if (state.changeHandler)
      document.removeEventListener('change', state.changeHandler, true);
    if (state.blurHandler)
      document.removeEventListener('blur', state.blurHandler, true);
    state.clickHandler = undefined;
    state.changeHandler = undefined;
    state.blurHandler = undefined;
    if (state.gtagCheckId) {
      clearInterval(state.gtagCheckId);
      state.gtagCheckId = undefined;
    }
    // Optional: remove our API to avoid calls after unmount
    if (isOwner && (globalThis as any).window.gtmTrack) {
      delete (globalThis as any).window.gtmTrack;
    }
    if (isOwner) {
      delete (globalThis as any).window.__EA_ANALYTICS_INIT__;
      state.instanceKey = undefined;
    }
  };
}

/////////////////////////////////////////////////////////////////

export const trackGA4Event = (customObject: { [name: string]: any }) => {
  log.debug(`Analytics object:`, JSON.stringify(customObject));
  globalThis.window.dataLayer.push({ event: 'custom_event', ...customObject });
};

export const trackGA4PageView = (data: { [name: string]: any }) => {
  globalThis.window.dataLayer.push(data);
};

export const trackFSVars = (userVars?: { [key: string]: any }) => {
  if (
    globalThis.window.FS &&
    typeof globalThis.window.FS.setUserVars === 'function' &&
    userVars
  ) {
    globalThis.window.FS.setUserVars(userVars);
  } else {
    log.error(`Failed to setUserVars.`, { traits: userVars });
  }
};
